import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { requestWS } from '../lib/utils';

export interface UseInitProps {
    token?: string;
    origin: string; // 'site' | 'courtier' | 'conseiller' | 'concessionnaire'
    key?: string | null;
    expire?: number; // in seconds
}

export interface WsHooksOptions {
    onError?: (error: Error) => void;
}

const createErrorHandler = (onError?: (error: Error) => void) => (error: Error) => {
    onError?.(error);
    throw error;
};

export const useInit = <TData = Record<string, unknown>>(
    { token, origin, key, expire }: UseInitProps,
    { onError }: WsHooksOptions = {}
): UseQueryResult<TData> => {
    return useQuery({
        queryKey: ['init', { token, origin, key }],
        queryFn: () => requestWS.post<TData>('/init', { token, origin, key }).then((res) => res.data).catch(createErrorHandler(onError)),
        staleTime: expire ?? 60 * 60, // 1 hour
    });
};

export const useQueryGetTarif = <TData = Record<string, unknown>>(
    token: string,
    data: Record<string, unknown>,
    refetchOnMount = true,
    { onError }: WsHooksOptions = {}
): UseQueryResult<TData> => {
    return useQuery({
        queryKey: ['get_tarif', { token }],
        queryFn: () => requestWS.post<TData>(`/get_tarif/${token}`, data).then((res) => res.data).catch(createErrorHandler(onError)),
        staleTime: 0,
        refetchOnMount: refetchOnMount,
    });
};

export const useMutationFill = <TData = Record<string, unknown>, TVariables = Record<string, unknown>>(
    token: string,
    { onError }: WsHooksOptions = {}
): UseMutationResult<TData, Error, TVariables> => {
    return useMutation({
        mutationFn: (data: TVariables) => requestWS.post<TData>(`/fill/${token}`, data).then((res) => res.data).catch(createErrorHandler(onError))
    });
};

export const useMutationGetTarif = <TData = Record<string, unknown>, TVariables = Record<string, unknown>>(
    token: string,
    { onError }: WsHooksOptions = {}
): UseMutationResult<TData, Error, TVariables> => {
    return useMutation({
        mutationFn: (data: TVariables) => requestWS.post<TData>(`/get_tarif/${token}`, data).then((res) => res.data).catch(createErrorHandler(onError))
    });
};

export const useMutationSaveDevis = <TData = Record<string, unknown>, TVariables = Record<string, unknown>>(
    token: string,
    { onError }: WsHooksOptions = {}
): UseMutationResult<TData, Error, TVariables> => {
    return useMutation({
        mutationFn: (data: TVariables) => requestWS.post<TData>(`/save_devis/${token}`, data).then((res) => res.data).catch(createErrorHandler(onError))
    });
};

export const useMutationPay = <TData = Record<string, unknown>, TVariables = Record<string, unknown>>(
    token: string,
    { onError }: WsHooksOptions = {}
): UseMutationResult<TData, Error, TVariables> => {
    return useMutation({
        mutationFn: (data: TVariables) => requestWS.post<TData>(`/pay/${token}`, data).then((res) => res.data).catch(createErrorHandler(onError))
    });
};

export interface PayEmbeddedResponse {
    id: number;
    amount: number;
    formToken: string;
    publicKey: string;
    endpoint?: string | null;
}

export const useMutationPayEmbedded = <TVariables = Record<string, unknown>>(
    token: string,
    { onError }: WsHooksOptions = {}
): UseMutationResult<PayEmbeddedResponse, Error, TVariables> => {
    return useMutation({
        mutationFn: (data: TVariables) => requestWS
            .post<PayEmbeddedResponse>(`/pay-embedded/${token}`, data)
            .then((res) => res.data)
            .catch(createErrorHandler(onError))
    });
};

export const useMutationBesoinAide = <TData = Record<string, unknown>, TVariables = Record<string, unknown>>(
    { onError }: WsHooksOptions = {}
): UseMutationResult<TData, Error, TVariables> => {
    return useMutation({
        mutationFn: (data: TVariables) => requestWS.post<TData>(`/besoin-d-aide`, data).then((res) => res.data).catch(createErrorHandler(onError))
    });
};

export interface LogConsentPayload {
    token: string;
    canal: string;
    phone: string;
    email: string;
    email_contact_consent?: boolean;
    phone_contact_consent?: boolean;
    type: 'souscription';
}

export const useMutationLogConsent = (
    { onError }: WsHooksOptions = {}
): UseMutationResult<unknown, Error, LogConsentPayload> => {
    return useMutation({
        mutationFn: (payload: LogConsentPayload) =>
            requestWS.post('/consent/log', payload).then((res) => res.data).catch(createErrorHandler(onError))
    });
};
